'use client'

import { BUILDER_EDIT_MODES, ROUTES } from 'constants/routesConstants'
import { useSetAtom } from 'jotai'
import { useSearchParams } from 'next/navigation'
import { ReactElement, useEffect, useTransition } from 'react'

import { getStore } from '@/store'

import {
	captureBuilderEvent,
	STEP_TO_INDEX,
	STEP_TO_NAME,
} from '../../builderAnalytics'
import {
	resetContentFormSections,
	resetContentFormValidity,
} from '../../contentFormSections/resetContentFormSections'
import {
	activeContentTypeAtom,
	altLandingQrTypeAtom,
	hasLandedFromAltLandingAtom,
	qrTypeAtom,
} from '../../generatorState'
import {
	GENERATOR_SEARCH_PARAM,
	PARAM_TO_QR_TYPE,
	TQrTypeParam,
} from '../../generatorTypes'
import { QrTypeCardDummy } from './QrTypeCardDummy'
import { TQrTypeCardProps } from './QrTypeCardTypes'

export type TGeneratorType = {
	name: string
	description: string
	icon: ReactElement
	url: `${typeof ROUTES.generator.typeStep}/${TQrTypeParam}`
	badge?: string
}

export const QrTypeCard = ({
	type,
	className,
	rendersOn,
	centeredLayout = false,
	onHoverStart,
	onHoverEnd,
	onClick,
}: TQrTypeCardProps) => {
	const searchParams = useSearchParams()

	const [, startTransition] = useTransition()

	const setQrType = useSetAtom(qrTypeAtom)
	const setAltLandingQrType = useSetAtom(altLandingQrTypeAtom)
	const setHasLandedFromAltLanding = useSetAtom(hasLandedFromAltLandingAtom)

	const targetQrType = searchParams.get(GENERATOR_SEARCH_PARAM.Target)
	const isChangeTypeMode =
		targetQrType !== null &&
		searchParams.get(GENERATOR_SEARCH_PARAM.Edit) ===
			BUILDER_EDIT_MODES.TYPE
	const urlQuery = isChangeTypeMode
		? `?${GENERATOR_SEARCH_PARAM.Target}=${targetQrType}&edit=${BUILDER_EDIT_MODES.TYPE}`
		: ''

	useEffect(() => {
		if (type.badge) {
			const cardQrType =
				(type.url.match(/[^/]+\/?$/)?.[0] as TQrTypeParam) || null
			captureBuilderEvent('builder_view_badge', {
				qr_type: cardQrType ? PARAM_TO_QR_TYPE[cardQrType] : null,
				step_index: STEP_TO_INDEX.type,
				step_name: STEP_TO_NAME.type,
				badge_type: 'most_used',
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleMouseEnter = () => {
		const qrType =
			(type.url.match(/[^/]+\/?$/)?.[0] as TQrTypeParam) || null
		onHoverStart?.(PARAM_TO_QR_TYPE[qrType])
	}

	const handleClick = () => {
		onClick?.()
		const qrType =
			(type.url.match(/[^/]+\/?$/)?.[0] as TQrTypeParam) || null

		if (qrType) {
			const newQrType = PARAM_TO_QR_TYPE[qrType]

			// MYQRCODE-10778 AC#13: qr_type passed explicitly — setQrType below is
			// deferred inside startTransition, so qrTypeAtom hasn't updated yet.
			captureBuilderEvent('builder_select_type', {
				qr_type: newQrType,
				section: 'wizard_content',
			})
			captureBuilderEvent('builder_step_complete', {
				qr_type: newQrType,
				step_index: STEP_TO_INDEX.type,
				step_name: STEP_TO_NAME.type,
				section: 'wizard_stepper',
			})

			const activeContentType = getStore().get(activeContentTypeAtom)
			if (activeContentType !== newQrType) {
				// Safe to reset validity here too (unlike ContentForm's own mount
				// effect): this runs before Step 2 navigates, so no section has
				// mounted yet to have its registration wiped out from under it.
				resetContentFormValidity()
				resetContentFormSections()
			} else {
				// Same type re-selected after a Step 1 round trip: content is
				// intentionally preserved, but section-validity bookkeeping from the
				// previous visit must still be cleared. Otherwise a stale isValid
				// entry can combine with a freshly-mounted section's immediate isValid
				// (e.g. the Business Page logo field, which reports valid on mount
				// regardless of user input) to make useCreateQrOnFirstValid see the
				// whole form as valid before every section has re-registered.
				resetContentFormValidity()
			}
		}

		startTransition(() => {
			if (qrType) {
				setQrType(PARAM_TO_QR_TYPE[qrType])
			}
		})

		if (rendersOn === ROUTES.generator.typeStep) {
			setHasLandedFromAltLanding(false)
		} else {
			setHasLandedFromAltLanding(true)
			setAltLandingQrType(PARAM_TO_QR_TYPE[qrType])
		}
	}

	return (
		<QrTypeCardDummy
			ref={(el) => {
				if (el) {
					requestAnimationFrame(() => {
						const isHovered = el.matches(':hover')
						if (isHovered) {
							handleMouseEnter()
						}
					})
				}
			}}
			type={type}
			urlQuery={urlQuery}
			centeredLayout={centeredLayout}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={onHoverEnd}
			onClick={handleClick}
			className={className}
		/>
	)
}
